/*
 * antd v5 pickers are dayjs-based by default. This app keeps moment as its
 * date library (moment values flow through forms, GraphQL payloads and
 * validation), so we generate moment-based pickers once here and every screen
 * imports DatePicker/TimePicker from this module instead of 'antd'.
 * Uses antd/es + rc-picker/es because webpack resolves the main 'antd' import
 * to antd/es (the package "module" field). Importing antd/lib here would
 * bundle a second copy of antd with its own React contexts.
 */
import generatePicker from 'antd/es/date-picker/generatePicker'
import moment from 'moment'
import momentGenerateConfig from 'rc-picker/es/generate/moment'
import * as React from 'react'

const BaseDatePicker = generatePicker(momentGenerateConfig)

/*
 * antd v5 returns a clicked date at 00:00 local time, while antd v4 carried
 * the current time. Many screens turn the picked value into a date through
 * UTC (moment.utc(value), toISOString().split('T')[0]), so 00:00 JST becomes
 * the previous day. Moving the time to midday keeps the same calendar date in
 * both local time and UTC.
 */
const toMidday = value =>
  moment.isMoment(value)
    ? value.clone().set({ hour: 12, minute: 0, second: 0, millisecond: 0 })
    : value

const withMiddayOnChange = onChange =>
  onChange &&
  ((value, dateString) =>
    onChange(
      Array.isArray(value) ? value.map(toMidday) : toMidday(value),
      dateString
    ))

const DatePicker = React.forwardRef((props, ref) => (
  <BaseDatePicker
    {...props}
    onChange={withMiddayOnChange(props.onChange)}
    ref={ref}
  />
))
DatePicker.displayName = 'DatePicker'

DatePicker.RangePicker = React.forwardRef((props, ref) => (
  <BaseDatePicker.RangePicker
    {...props}
    onChange={withMiddayOnChange(props.onChange)}
    ref={ref}
  />
))
DatePicker.RangePicker.displayName = 'DatePicker.RangePicker'

/*
 * antd v5 calls a legacy onSelect for each clicked hour/minute only on its own
 * DatePicker.TimePicker. Screens commit the time in onSelect (as they did on
 * antd v4), so build on that picker instead of <DatePicker picker='time'>.
 */
const TimePicker = React.forwardRef((props, ref) => (
  <BaseDatePicker.TimePicker {...props} mode={undefined} ref={ref} />
))
TimePicker.displayName = 'TimePicker'

TimePicker.RangePicker = React.forwardRef((props, ref) => (
  <BaseDatePicker.RangePicker {...props} picker='time' ref={ref} />
))
TimePicker.RangePicker.displayName = 'TimePicker.RangePicker'

export { DatePicker, TimePicker }
